import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import { DeleteForeverRounded } from "@mui/icons-material";
import { notaFiscalProduto } from "../../../interfaces/notaFiscal/notaFiscalProduto";
import HoverToolTip from "../../ToolTips/HoverToolTip/HoverToolTip";
import CustomIcon from "../../CustomIcon/CustomIcon";
import CustomTableActionButton from "./CustomTableActionButton";
import CustomTableEditableCell from "./CustomTableEditableCell";
import * as Styles from "../CustomTable.styles";
import { composeUniqueKey } from "../../../utils/notaFiscalUtils";

interface CustomTableRowProps {
  row: notaFiscalProduto;
  index: number;
  rowQcomFormatted: string;
  isItemPresent: boolean;
  isItemTotalEqual: boolean;
  showRemoveColumn: boolean;
  isScanMode: boolean;
  hasPrimaryData: boolean;
  hasSecondaryData: boolean;
  hasEmptyData: boolean;
  hasRemovedItem: boolean;
  isGettingCounter: boolean;
  lastUpdatedProductUniqueKey: string;
  handleOnBlinkAnimationEnd: () => void;
  handleOpenDialog: () => void;
  setSelectedItem: (item: notaFiscalProduto) => void;
  rowRef: (element: HTMLTableRowElement | null) => void;
  onEditProductName?: (uniqueKey: string, newName: string) => void;
  isEditAllowed: boolean;
}

export default function CustomTableRow({
  row,
  rowQcomFormatted,
  isItemPresent,
  isItemTotalEqual,
  showRemoveColumn,
  isScanMode,
  hasPrimaryData,
  hasSecondaryData,
  hasEmptyData,
  hasRemovedItem,
  isGettingCounter,
  lastUpdatedProductUniqueKey,
  handleOnBlinkAnimationEnd,
  handleOpenDialog,
  setSelectedItem,
  rowRef,
  onEditProductName,
  isEditAllowed,
}: CustomTableRowProps) {
  const canEditCell = isScanMode && hasPrimaryData && hasSecondaryData && isEditAllowed;

  const rowState = Styles.getRowUIState({
    hasEmptyData,
    hasRemovedItem,
    hasSecondaryData,
    isItemPresent,
    isItemTotalEqual,
    isGettingCounter,
  });

  function isShowingStatusIcon() {
    if (isGettingCounter && !isItemPresent) return false;
    return true;
  }

  return (
    <Styles.CustomBodyRow
      ref={rowRef}
      onAnimationEnd={handleOnBlinkAnimationEnd}
      uiState={rowState}
      blink={lastUpdatedProductUniqueKey === composeUniqueKey(row)}
    >
      <Styles.CustomBodyCell component="th" scope="row">
        {row.cProd}
      </Styles.CustomBodyCell>

      <Styles.CustomBodyCell component="th" scope="row">
        {row.cEAN}
      </Styles.CustomBodyCell>

      <CustomTableEditableCell
        value={row.xProd}
        isEditAllowed={canEditCell}
        onChange={(newValue) => {
          if (onEditProductName) {
            onEditProductName(composeUniqueKey(row), newValue);
          }
        }}
      />

      <Styles.CustomBodyCell align="center">{row.uCom}</Styles.CustomBodyCell>

      <Styles.CustomBodyCell align="center">
        <Styles.CenteredCellContent>{rowQcomFormatted}</Styles.CenteredCellContent>
      </Styles.CustomBodyCell>

      <Styles.CustomBodyCell align={showRemoveColumn ? "center" : "left"} sx={{ padding: 0 }}>
        {hasPrimaryData && hasSecondaryData && isShowingStatusIcon() && rowState.tooltipTitle && (
          <Styles.StatusIconContainer align={isScanMode ? "center" : "left"}>
            <HoverToolTip content={rowState.tooltipTitle}>
              <CustomIcon
                Icon={isItemTotalEqual ? CheckCircleIcon : InfoIcon}
                color={rowState.iconColor}
                hoverColor={rowState.iconHoverColor}
                size="1.3rem"
              />
            </HoverToolTip>
          </Styles.StatusIconContainer>
        )}
      </Styles.CustomBodyCell>

      {showRemoveColumn && (
        <Styles.CustomBodyCell align="left" sx={{ padding: 0 }}>
          {hasPrimaryData && hasSecondaryData && isShowingStatusIcon() && (
            <Styles.ActionsContainer elevation={1}>
              <CustomTableActionButton
                Icon={DeleteForeverRounded}
                hoverColor="var(--color-action-danger)"
                onClick={() => {
                  setSelectedItem(row);
                  handleOpenDialog();
                }}
              />
            </Styles.ActionsContainer>
          )}
        </Styles.CustomBodyCell>
      )}
    </Styles.CustomBodyRow>
  );
}
